/* eslint-disable max-len */
/* eslint-disable no-console */
import React, { useContext, useEffect, useRef, useState } from 'react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import DOMPurify from 'dompurify';
import { FaCode } from 'react-icons/fa';
import { Button, Typography, Dialog, DialogContent, Stack, Grid } from '@mui/material';
import { LanguageContext } from '../../../libs/Context';

const buttonStyle = {
	width          : 'var(--pixel24)',
	height         : 'var(--pixel24)',
	margin         : '0 2px',
	display        : 'flex',
	alignItems     : 'center',
	justifyContent : 'center'
};

const CustomToolbar = ({ isFullscreen, toggleFullscreen, openHtmlModal }) => (
	<Grid container id='toolbar' direction={'row'} display={'flex'} flexDirection={'row'} width={'var(--percent100)'} justifyContent={'space-between'} sx={{ borderRadius: '8px 8px 0 0' }}>
		<Grid item xs={11}>
			<select className='ql-header' defaultValue=''>
				<option value='1' />
				<option value='2' />
				<option value='' />
			</select>
			<select className='ql-font'>
				<option value='' />
				<option value='serif' />
				<option value='monospace' />
			</select>
			<button className='ql-bold' style={buttonStyle} />
			<button className='ql-italic' style={buttonStyle} />
			<button className='ql-underline' style={buttonStyle} />
			<button className='ql-strike' style={buttonStyle} />
			<button className='ql-blockquote' style={buttonStyle} />
			<button className='ql-list' value='ordered' style={buttonStyle} />
			<button className='ql-list' value='bullet' style={buttonStyle} />
			<button className='ql-indent' value='-1' style={buttonStyle} />
			<button className='ql-indent' value='+1' style={buttonStyle} />
			<button className='ql-link' style={buttonStyle} />
			<button className='ql-image' style={buttonStyle} />
			<button className='ql-clean' style={buttonStyle} />
			<button className='ql-html' onClick={openHtmlModal} style={buttonStyle}>
				<FaCode style={{ fontSize: 'var(--pixel16)' }} />
			</button>
		</Grid>
		<Grid item xs={1} display={'flex'} justifyContent={'end'}>
			<button className='ql-fullscreen' onClick={toggleFullscreen}>
				{isFullscreen
					? <img src='/images/icons/zoomouticon.svg' alt='' style={{ filter: 'invert(50%) brightness(10%)', width: 'var(--pixel16)', height: 'var(--pixel16)' }} />
					: <img src='/images/icons/zoominicon.svg' alt='' style={{ filter: 'invert(50%) brightness(10%)', width: 'var(--pixel16)', height: 'var(--pixel16)' }} />}
			</button>
		</Grid>
	</Grid>
);
const FullscreenEditor = ({ value, onChange, reactQuillRef, toggleFullscreen, openHtmlModal }) =>
{
	const { language } = useContext(LanguageContext);

	return (
		<Dialog open fullWidth maxWidth='lg' onClose={toggleFullscreen}>
			<DialogContent sx={{ display: 'flex', flexDirection: 'column', height: '80vh' }}>
				<CustomToolbar
					isFullscreen
					toggleFullscreen={toggleFullscreen}
					openHtmlModal={openHtmlModal}
				/>
				<ReactQuill
					theme='snow'
					modules={{ clipboard: { matchVisual: false }, toolbar: { container: '#toolbar' } }}
					formats={[
						'header', 'font', 'size', 'bold', 'italic', 'underline', 'strike',
						'blockquote', 'list', 'bullet', 'indent', 'link', 'image'
					]}
					ref={reactQuillRef}
					placeholder={language?.enterWebinarDescription}
					style={{ flex: 1 }}
					value={value}
					onChange={onChange}
				/>
			</DialogContent>
		</Dialog>
	);
};

const HtmlQuillEditor = (props) =>
{
	const { text, onChange, maxLength = Infinity } = props;
	const { language } = useContext(LanguageContext);
	const reactQuillRef = useRef<ReactQuill | null>(null);
	const [ isFullscreen, setIsFullscreen ] = useState(false);
	const [ htmlDialogOpen, setHtmlDialogOpen ] = useState(false);

	useEffect(() =>
	{
		// Create a style element
		const style = document.createElement('style');

		style.innerHTML = `
      .ql-editor.ql-blank::before {
        font-style: normal !important;
		font-size: 14px !important;
      }
      .ql-editor {
        font-style: normal !important;
		font-size: 14px !important;
      }
    `;
		document.head.appendChild(style);

		return () =>
		{
			document.head.removeChild(style);
		};
	}, []);

	const openHtmlModal = () => setHtmlDialogOpen(true);
	const closeHtmlModal = () => setHtmlDialogOpen(false);

	const handleSave = () =>
	{
		const textarea = document.getElementById('htmlInput') as HTMLTextAreaElement;

		if (textarea)
		{
			const htmlContent = textarea.value;
			const sanitizedHtml = DOMPurify.sanitize(htmlContent, {
				USE_PROFILES : { html: true }
			});

			if (reactQuillRef.current)
			{
				const quill = reactQuillRef.current.getEditor();
				const range = quill.getSelection(true);

				if (range)
				{
					quill.clipboard.dangerouslyPasteHTML(range.index, sanitizedHtml);
				}
				else
				{
					console.warn('No selection range found');
				}
			}
			else
			{
				console.warn('Quill editor reference is not set');
			}
			closeHtmlModal();
			if (isFullscreen)
			{
				setIsFullscreen(true);
			}
		}
	};

	const handleOnChange = (e) =>
	{
		const editor = reactQuillRef.current?.getEditor(); // Access the Quill instance

		if (editor)
		{
			const length = editor.getLength() - 1; // Subtract 1 to exclude the trailing newline character

			if (length <= maxLength)
			{
				onChange(e);
			}
			else onChange(e.slice(0, maxLength + 3));
		}
	};

	const toggleFullscreen = () => setIsFullscreen(!isFullscreen);

	return (
		<>
			{!isFullscreen && (
				<>
					<CustomToolbar
						isFullscreen={false}
						toggleFullscreen={toggleFullscreen}
						openHtmlModal={openHtmlModal}
					/>
					<Grid container>
						<ReactQuill
							theme='snow'
							modules={{ clipboard: { matchVisual: false }, toolbar: { container: '#toolbar' } }}
							formats={[
								'header', 'font', 'size', 'bold', 'italic', 'underline', 'strike',
								'blockquote', 'list', 'bullet', 'indent', 'link', 'image'
							]}
							ref={reactQuillRef}
							value={text}
							onChange={handleOnChange}
							placeholder={props.desc || language?.enterWebinarDescription}
							className='fullscreen-quill'
							style={{ maxHeight: 'var(--font200)', overflowY: 'auto' }}
						/>
					</Grid>
				</>
			)}
			{isFullscreen && (
				<FullscreenEditor
					value={text}
					onChange={handleOnChange}
					reactQuillRef={reactQuillRef}
					toggleFullscreen={toggleFullscreen}
					openHtmlModal={openHtmlModal}
				/>
			)}
			<Dialog open={htmlDialogOpen} fullWidth maxWidth='lg' onClose={closeHtmlModal}>
				<DialogContent sx={{ display: 'flex', flexDirection: 'column', height: '80vh' }}>
					<Typography variant='inherit' fontWeight='var(--font500)' pb='var(--pixel8)'>Enter HTML Code</Typography>
					<textarea id='htmlInput' style={{ flex: 1, width: 'var(--percent100)', resize: 'none', padding: 'var(--pixel10)', boxSizing: 'border-box' }} />
					<Stack direction={'row'} spacing={1} pt='var(--pixel16)' width={'var(--percent100)'} display={'flex'} justifyContent={'end'}>
						<Button variant='outlined' onClick={closeHtmlModal}>Cancel</Button>
						<Button variant='contained' sx={{ color: 'var(--whiteColor)' }} onClick={handleSave}>Insert HTML</Button>
					</Stack>
				</DialogContent>
			</Dialog>
		</>
	);
};

export default HtmlQuillEditor;
